import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
} from "@chakra-ui/react";
import { Calendar, MapPin } from "lucide-react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const Experience: React.FC = () => {
  // Dark mode color values
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const descriptionTextColor = useColorModeValue("gray.700", "gray.200");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const companyColor = useColorModeValue("brand.500", "brand.300");
  const cardBoxShadow = useColorModeValue("md", "2xl");
  const cardBoxShadowHover = useColorModeValue("xl", "dark-lg");

  const experiences = [
    {
      title: "Frontend Developer",
      company: "VeendHQ",
      location: "Lagos State, Nigeria",
      period: "August 2022 - Present",
      description: [
        "Developed and maintained responsive, high-performance web applications using React.js, Next.js, and Chakra UI with TypeScript",
        "Built reusable UI components to enhance design consistency and streamline development workflows",
        "Contributed to scalable frontend architecture, focusing on performance optimization and maintainability",
        "Collaborated cross-functionally with UI/UX designers and backend engineers to deliver user-centric and scalable features",
        "Integrated RESTful APIs, optimizing data fetching and rendering for improved application responsiveness and SEO",
      ],
      technologies: [
        "React.js",
        "Next.js",
        "TypeScript",
        "Chakra UI",
        "Redux",
        "React Native",
        "RESTful APIs",
      ],
      url: "https://veendhq.com",
    },
    {
      title: "Frontend Developer",
      company: "US ProTech",
      location: "Las Vegas, Nevada, United States",
      period: "June 2022 - September 2022",
      description: [
        "Collaborated with cross-functional team members to rebuild and revamp Anamo Inc.'s product (Version 2.0)",
        "Contributed to the successful launch by fostering team synergy and collective problem-solving",
        "Played a key role in the rebuild and redesign of Anamo Inc.'s flagship product (v2.0), improving usability and performance",
        "Worked closely with product managers, designers, and engineers to ensure a successful and timely release",
      ],
      technologies: [
        "HTML5",
        "CSS3",
        "JavaScript",
        "jQuery",
        "SCSS",
        "Bootstrap",
        "Team Collaboration",
      ],
      url: "",
    },
    {
      title: "Frontend Developer",
      company: "Anamo Inc",
      location: "Las Vegas, Nevada, United States",
      period: "June 2022 - September 2022",
      description: [
        "Focused on improving the User Interface (UI) and User Experience (UX) to create a more intuitive and visually appealing product",
        "Enhanced design and usability to align with evolving user needs and expectations",
        "Prioritized usability enhancements to improve customer satisfaction and user retention",
      ],
      technologies: [
        "HTML5",
        "CSS3",
        "JavaScript",
        "jQuery",
        "SCSS",
        "Bootstrap",
        "Team Collaboration",
      ],
      url: "",
    },
    {
      title: "Frontend Developer",
      company: "Sanwo",
      location: "Ibadan, Oyo, Nigeria",
      period: "July 2021 - Present",
      description: [
        "Specialized in frontend development for the Sanwo Escrow service system, focusing on building intuitive and responsive user interfaces",
        "Contributed to the development of UI components across three core products: Sanwo Classic, Sanwo Express, and Sanwo Travels",
        "Developed scalable and responsive frontend interfaces for the Sanwo Escrow platform, improving user engagement and usability",
      ],
      technologies: [
        "React",
        "Next.js",
        "TypeScript",
        "TailwindCSS",
        "Chakra UI",
        "Responsive Design",
        "Redux Toolkit",
      ],
      url: "https://sanwo.io",
    },
  ];

  return (
    <Box id="experience" py={20} bg={bgColor}>
      <Container maxW="7xl">
        <MotionBox
          textAlign="center"
          mb={16}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Heading
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="bold"
            color={textColor}
            mb={4}
          >
            Work Experience
          </Heading>
          <Text fontSize="xl" color={mutedTextColor} maxW="3xl" mx="auto">
            My professional journey in frontend development, building scalable
            applications and collaborating with cross-functional teams to
            deliver exceptional user experiences.
          </Text>
        </MotionBox>

        <VStack spacing={8} align="stretch">
          {experiences.map((exp, index) => (
            <MotionBox
              key={exp.title + exp.company}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <Box
                bg={cardBg}
                p={8}
                borderRadius="xl"
                boxShadow={cardBoxShadow}
                border="1px"
                borderColor={borderColor}
                position="relative"
                _hover={{
                  boxShadow: cardBoxShadowHover,
                  transform: "translateY(-4px)",
                }}
                transition="all 0.3s ease"
              >
                {index === 0 && (
                  <Badge
                    position="absolute"
                    top={4}
                    right={4}
                    colorScheme="green"
                    variant="solid"
                  >
                    Current
                  </Badge>
                )}

                <VStack align="start" spacing={4}>
                  <Box>
                    <Heading size="lg" color={textColor} mb={2}>
                      {exp.title}
                    </Heading>
                    <Text
                      fontSize="xl"
                      fontWeight="semibold"
                      color={companyColor}
                      mb={2}
                    >
                      {exp.company}
                    </Text>
                    <HStack spacing={4} color={mutedTextColor}>
                      <HStack>
                        <Calendar size={16} />
                        <Text fontSize="sm">{exp.period}</Text>
                      </HStack>
                      <HStack>
                        <MapPin size={16} />
                        <Text fontSize="sm">{exp.location}</Text>
                      </HStack>
                    </HStack>
                  </Box>

                  <VStack align="start" spacing={2}>
                    {exp.description.map((item, idx) => (
                      <HStack key={idx} align="start">
                        <Box
                          w={2}
                          h={2}
                          bg="brand.500"
                          borderRadius="full"
                          mt={2}
                        />
                        <Text color={descriptionTextColor}>{item}</Text>
                      </HStack>
                    ))}
                  </VStack>

                  <HStack spacing={2} flexWrap="wrap">
                    {exp.technologies.map((tech) => (
                      <Badge
                        key={tech}
                        colorScheme="brand"
                        variant="subtle"
                        fontSize="xs"
                      >
                        {tech}
                      </Badge>
                    ))}
                  </HStack>
                </VStack>
              </Box>
            </MotionBox>
          ))}
        </VStack>
      </Container>
    </Box>
  );
};

export default Experience;
